import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEventsEventsV3EventManagementUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/events/v3/events',
        destPath: '/v3/events',
      },
      {
        srcPath: '/events/v3/bulk/events',
        destPath: '/v3/bulk/events',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/events-events-v3-adapter',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/events-events-v3-adapter',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_events_wix-events-v-2';

/**
 * Creates an event. <br> <br>
 * The event includes a default registration form in the selected language, which consists of input fields for first name, last name, and email. See the [Registration Form API](https://dev.wix.com/docs/rest/business-solutions/events/form/about-the-registration-form-api) for more information. <br> <br>
 * You can create the event as a draft by setting `draft` to `true`. Otherwise, the event is published right away. <br> <br>
 * To create a recurring event, set `individualEventDates` in the `dateAndTimeSettings.recurringEvents` object. <br> <br>
 * The event is automatically set up to send daily summary reports of new guests to your business email.
 */
export function createEvent(payload: object): RequestOptionsFactory<any> {
  function __createEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'event.createdDate' },
          { path: 'event.updatedDate' },
          { path: 'event.publishedDate' },
          { path: 'event.dateAndTimeSettings.startDate' },
          { path: 'event.dateAndTimeSettings.endDate' },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
          },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
          },
          { path: 'event.mainImage.urlExpirationDate' },
          { path: 'event.registration.rsvp.startDate' },
          { path: 'event.registration.rsvp.endDate' },
          { path: 'event.categories.categories.createdDate' },
          { path: 'event.description.metadata.createdTimestamp' },
          { path: 'event.description.metadata.updatedTimestamp' },
          { path: 'event.badges.badges.functionUpdateDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'event.location.address.geocode.latitude' },
          { path: 'event.location.address.geocode.longitude' },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'event.description.nodes.galleryData.options.item.ratio' },
          { path: 'event.description.nodes.imageData.image.duration' },
          { path: 'event.description.nodes.mapData.mapSettings.lat' },
          { path: 'event.description.nodes.mapData.mapSettings.lng' },
          { path: 'event.description.nodes.pollData.poll.image.duration' },
          {
            path: 'event.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'event.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'event.description.nodes.appEmbedData.image.duration' },
          { path: 'event.description.nodes.videoData.video.duration' },
          { path: 'event.description.nodes.videoData.thumbnail.duration' },
          { path: 'event.description.nodes.audioData.audio.duration' },
          { path: 'event.description.nodes.audioData.coverImage.duration' },
          {
            path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'event.description.nodes.shapeData.shape.duration' },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'event.description.nodes.tocData.fontSize' },
          { path: 'event.description.nodes.tocData.itemSpacing' },
          { path: 'event.description.nodes.smartBlockCellData.shape.duration' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.CreateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createEvent;
}

/**
 * Clones an event, including the registration form, notifications, multilingual translations and ticket configuration from the original event. <br> <br>
 * The new event's date is automatically set to 14 days from the original event date. <br> <br>
 * If an event with the same `title` already exists, the new event's `title` gets a sequence number. For example, if you clone an event named `"Leather Crafting 101"`, the new event's title is `"Leather Crafting 101 (1)"`. <br> <br>
 * You can change the required entity field values while cloning an event.
 */
export function cloneEvent(payload: object): RequestOptionsFactory<any> {
  function __cloneEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'event.createdDate' },
          { path: 'event.updatedDate' },
          { path: 'event.publishedDate' },
          { path: 'event.dateAndTimeSettings.startDate' },
          { path: 'event.dateAndTimeSettings.endDate' },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
          },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
          },
          { path: 'event.mainImage.urlExpirationDate' },
          { path: 'event.registration.rsvp.startDate' },
          { path: 'event.registration.rsvp.endDate' },
          { path: 'event.categories.categories.createdDate' },
          { path: 'event.description.metadata.createdTimestamp' },
          { path: 'event.description.metadata.updatedTimestamp' },
          { path: 'event.badges.badges.functionUpdateDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'event.location.address.geocode.latitude' },
          { path: 'event.location.address.geocode.longitude' },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'event.description.nodes.galleryData.options.item.ratio' },
          { path: 'event.description.nodes.imageData.image.duration' },
          { path: 'event.description.nodes.mapData.mapSettings.lat' },
          { path: 'event.description.nodes.mapData.mapSettings.lng' },
          { path: 'event.description.nodes.pollData.poll.image.duration' },
          {
            path: 'event.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'event.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'event.description.nodes.appEmbedData.image.duration' },
          { path: 'event.description.nodes.videoData.video.duration' },
          { path: 'event.description.nodes.videoData.thumbnail.duration' },
          { path: 'event.description.nodes.audioData.audio.duration' },
          { path: 'event.description.nodes.audioData.coverImage.duration' },
          {
            path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'event.description.nodes.shapeData.shape.duration' },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'event.description.nodes.tocData.fontSize' },
          { path: 'event.description.nodes.tocData.itemSpacing' },
          { path: 'event.description.nodes.smartBlockCellData.shape.duration' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.CloneEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{eventId}/clone',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cloneEvent;
}

/** Updates an event. */
export function updateEvent(payload: object): RequestOptionsFactory<any> {
  function __updateEvent({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldmask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'event.createdDate' },
          { path: 'event.updatedDate' },
          { path: 'event.publishedDate' },
          { path: 'event.dateAndTimeSettings.startDate' },
          { path: 'event.dateAndTimeSettings.endDate' },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
          },
          {
            path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
          },
          { path: 'event.mainImage.urlExpirationDate' },
          { path: 'event.registration.rsvp.startDate' },
          { path: 'event.registration.rsvp.endDate' },
          { path: 'event.categories.categories.createdDate' },
          { path: 'event.description.metadata.createdTimestamp' },
          { path: 'event.description.metadata.updatedTimestamp' },
          { path: 'event.badges.badges.functionUpdateDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'event.location.address.geocode.latitude' },
          { path: 'event.location.address.geocode.longitude' },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'event.description.nodes.galleryData.options.item.ratio' },
          { path: 'event.description.nodes.imageData.image.duration' },
          { path: 'event.description.nodes.mapData.mapSettings.lat' },
          { path: 'event.description.nodes.mapData.mapSettings.lng' },
          { path: 'event.description.nodes.pollData.poll.image.duration' },
          {
            path: 'event.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'event.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'event.description.nodes.appEmbedData.image.duration' },
          { path: 'event.description.nodes.videoData.video.duration' },
          { path: 'event.description.nodes.videoData.thumbnail.duration' },
          { path: 'event.description.nodes.audioData.audio.duration' },
          { path: 'event.description.nodes.audioData.coverImage.duration' },
          {
            path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'event.description.nodes.shapeData.shape.duration' },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'event.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'event.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'event.description.nodes.tocData.fontSize' },
          { path: 'event.description.nodes.tocData.itemSpacing' },
          { path: 'event.description.nodes.smartBlockCellData.shape.duration' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'PATCH' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.UpdateEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{event.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateEvent;
}

/**
 * Publishes a draft event to your live site. Once published, the event's status changes from `"DRAFT"` to `"UPCOMING"`. <br> <br>
 * It's impossible to revert the `"DRAFT"` status after publishing. The only option is to clone the event, then delete the original.
 */
export function publishDraftEvent(payload: object): RequestOptionsFactory<any> {
  function __publishDraftEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.PublishDraftEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{eventId}/publish',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __publishDraftEvent;
}

/**
 * Cancels an event. <br> <br>
 * After cancellation, registration for an event is closed. To reuse the event, call Clone Event and Publish Event to publish it again. <br> <br>
 * If event cancellation notifications are enabled, canceling an event automatically triggers the sending of cancellation emails and/or push notifications to registered guests.
 */
export function cancelEvent(payload: object): RequestOptionsFactory<any> {
  function __cancelEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.CancelEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{eventId}/cancel',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelEvent;
}

/**
 * Cancels multiple events that meet the specified criteria. <br> <br>
 * After cancellation, registration for an event is closed. To reuse the event, call Clone Event and Publish Event to publish it again. <br> <br>
 * If event cancellation notifications are enabled, canceling an event automatically triggers the sending of cancellation emails and/or push notifications to registered guests.
 */
export function bulkCancelEventsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCancelEventsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn:
        'wix.events.events.v3.EventManagement.BulkCancelEventsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/bulk/events/cancel-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkCancelEventsByFilter;
}

/**
 * Deletes an event. <br> <br>
 * You can retrieve the deleted event through a GDPR access request.
 */
export function deleteEvent(payload: object): RequestOptionsFactory<any> {
  function __deleteEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'DELETE' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.DeleteEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{eventId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteEvent;
}

/**
 * Deletes multiple events that meet the specified criteria. <br> <br>
 * You can retrieve the deleted events through a GDPR access request.
 */
export function bulkDeleteEventsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteEventsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn:
        'wix.events.events.v3.EventManagement.BulkDeleteEventsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/bulk/events/delete-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteEventsByFilter;
}

/**
 * Creates a query to retrieve a list of events.
 *
 *
 * The `queryEvents()` method builds a query to retrieve a list of events and returns a [`EventsQueryBuilder`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder) object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/find) method.
 *
 * You can refine the query by chaining `EventsQueryBuilder` methods onto the query. `EventsQueryBuilder` methods enable you to sort, filter, and control the results `queryEvents()` returns.
 *
 * `queryEvents()` runs with these `EventsQueryBuilder` defaults, which you can override:
 *
 * - [`skip(0)`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/skip)
 * - [`limit(50)`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/limit)
 * - [`descending("_createdDate")`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/descending)
 *
 * The methods that are chained to `queryEvents()` are applied in the order they're called. For example, if you apply `ascending('title')` and then `descending('status')`, the results are sorted first by the `title`, and then, if there are multiple results with the same `title`, the items are sorted by `status`.
 */
export function queryEvents(payload: object): RequestOptionsFactory<any> {
  function __queryEvents({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.QueryEvents',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.publishedDate' },
              { path: 'events.dateAndTimeSettings.startDate' },
              { path: 'events.dateAndTimeSettings.endDate' },
              {
                path: 'events.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'events.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'events.mainImage.urlExpirationDate' },
              { path: 'events.registration.rsvp.startDate' },
              { path: 'events.registration.rsvp.endDate' },
              { path: 'events.categories.categories.createdDate' },
              { path: 'events.description.metadata.createdTimestamp' },
              { path: 'events.description.metadata.updatedTimestamp' },
              { path: 'events.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'events.location.address.geocode.latitude' },
              { path: 'events.location.address.geocode.longitude' },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'events.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'events.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'events.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'events.description.nodes.imageData.image.duration' },
              { path: 'events.description.nodes.mapData.mapSettings.lat' },
              { path: 'events.description.nodes.mapData.mapSettings.lng' },
              { path: 'events.description.nodes.pollData.poll.image.duration' },
              {
                path: 'events.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'events.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'events.description.nodes.appEmbedData.image.duration' },
              { path: 'events.description.nodes.videoData.video.duration' },
              { path: 'events.description.nodes.videoData.thumbnail.duration' },
              { path: 'events.description.nodes.audioData.audio.duration' },
              {
                path: 'events.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'events.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'events.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'events.description.nodes.shapeData.shape.duration' },
              {
                path: 'events.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'events.description.nodes.tocData.fontSize' },
              { path: 'events.description.nodes.tocData.itemSpacing' },
              {
                path: 'events.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryEvents;
}

/** Counts events by status. */
export function countEventsByStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __countEventsByStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'POST' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.CountEventsByStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/count-by-status',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countEventsByStatus;
}

/** Retrieves a list of up to 100 events that belong to the same event category. */
export function listEventsByCategory(
  payload: object
): RequestOptionsFactory<any> {
  function __listEventsByCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'GET' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.ListEventsByCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/category/{categoryId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'events.createdDate' },
              { path: 'events.updatedDate' },
              { path: 'events.publishedDate' },
              { path: 'events.dateAndTimeSettings.startDate' },
              { path: 'events.dateAndTimeSettings.endDate' },
              {
                path: 'events.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'events.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'events.mainImage.urlExpirationDate' },
              { path: 'events.registration.rsvp.startDate' },
              { path: 'events.registration.rsvp.endDate' },
              { path: 'events.categories.categories.createdDate' },
              { path: 'events.description.metadata.createdTimestamp' },
              { path: 'events.description.metadata.updatedTimestamp' },
              { path: 'events.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'events.location.address.geocode.latitude' },
              { path: 'events.location.address.geocode.longitude' },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'events.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'events.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'events.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'events.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'events.description.nodes.imageData.image.duration' },
              { path: 'events.description.nodes.mapData.mapSettings.lat' },
              { path: 'events.description.nodes.mapData.mapSettings.lng' },
              { path: 'events.description.nodes.pollData.poll.image.duration' },
              {
                path: 'events.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'events.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'events.description.nodes.appEmbedData.image.duration' },
              { path: 'events.description.nodes.videoData.video.duration' },
              { path: 'events.description.nodes.videoData.thumbnail.duration' },
              { path: 'events.description.nodes.audioData.audio.duration' },
              {
                path: 'events.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'events.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'events.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'events.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'events.description.nodes.shapeData.shape.duration' },
              {
                path: 'events.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'events.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'events.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'events.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'events.description.nodes.tocData.fontSize' },
              { path: 'events.description.nodes.tocData.itemSpacing' },
              {
                path: 'events.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listEventsByCategory;
}

/** Retrieves an event by ID. */
export function getEvent(payload: object): RequestOptionsFactory<any> {
  function __getEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'GET' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.GetEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/{eventId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEvent;
}

/**
 * Retrieves an event by the slug URL.
 *
 * The slug is the end of an event URL that refers to a specific event. For example, if an event's URL is `https://example.com/events/event/{my-event-slug}`, the slug is `my-event-slug`.
 */
export function getEventBySlug(payload: object): RequestOptionsFactory<any> {
  function __getEventBySlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.events.v3.event',
      method: 'GET' as any,
      methodFqn: 'wix.events.events.v3.EventManagement.GetEventBySlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEventsEventsV3EventManagementUrl({
        protoPath: '/v3/events/slug/{slug}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'event.createdDate' },
              { path: 'event.updatedDate' },
              { path: 'event.publishedDate' },
              { path: 'event.dateAndTimeSettings.startDate' },
              { path: 'event.dateAndTimeSettings.endDate' },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
              },
              {
                path: 'event.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
              },
              { path: 'event.mainImage.urlExpirationDate' },
              { path: 'event.registration.rsvp.startDate' },
              { path: 'event.registration.rsvp.endDate' },
              { path: 'event.categories.categories.createdDate' },
              { path: 'event.description.metadata.createdTimestamp' },
              { path: 'event.description.metadata.updatedTimestamp' },
              { path: 'event.badges.badges.functionUpdateDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'event.location.address.geocode.latitude' },
              { path: 'event.location.address.geocode.longitude' },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'event.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'event.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'event.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'event.description.nodes.imageData.image.duration' },
              { path: 'event.description.nodes.mapData.mapSettings.lat' },
              { path: 'event.description.nodes.mapData.mapSettings.lng' },
              { path: 'event.description.nodes.pollData.poll.image.duration' },
              {
                path: 'event.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'event.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'event.description.nodes.appEmbedData.image.duration' },
              { path: 'event.description.nodes.videoData.video.duration' },
              { path: 'event.description.nodes.videoData.thumbnail.duration' },
              { path: 'event.description.nodes.audioData.audio.duration' },
              { path: 'event.description.nodes.audioData.coverImage.duration' },
              {
                path: 'event.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'event.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'event.description.nodes.shapeData.shape.duration' },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'event.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'event.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'event.description.nodes.tocData.fontSize' },
              { path: 'event.description.nodes.tocData.itemSpacing' },
              {
                path: 'event.description.nodes.smartBlockCellData.shape.duration',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getEventBySlug;
}
